import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Loading, Modal, CopyableId, ErrorState } from '@fiado/react-kit';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError, resolveErrorMessage } from '../../../api/errorMapping';
import { Banner } from '../../../components/ui/Banner';
import { TechnicalDetails } from '../../../components/shared/TechnicalDetails';
import { useApplications } from '../../../hooks/useApplications';
import { usePlatformAdminNames } from '../../../hooks/usePlatformAdminNames';
import { useResolvedTenantLevels } from '../../../hooks/useResolvedTenantLevels';
import { actorDisplayName } from '../../../lib/actorName';
import { formatDateMx } from '../../../lib/dateHelpers';
import { MFA_METHODS } from '../../../lib/securityPolicy';
import type { PlatformTenantStatusLocal } from '../../../api/types/PlatformLocal';
import type { MfaMethodLocal } from '../../../api/types/TenantLocal';

const STATUS_META: Record<
    PlatformTenantStatusLocal,
    { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }
> = {
    provisioning: { label: 'En provisión', variant: 'info' },
    active: { label: 'Activo', variant: 'success' },
    suspended: { label: 'Suspendido', variant: 'danger' },
    'pending-infra': { label: 'Pendiente de infraestructura', variant: 'warning' },
};

const MFA_METHOD_LABEL = new Map(MFA_METHODS.map((m) => [m.value, m.label]));

/** Nombres de los métodos de verificación permitidos, en el orden en que llegan. */
function mfaMethodNames(methods: MfaMethodLocal[] | undefined): string {
    if (!methods?.length) return '—';
    return methods.map((m) => MFA_METHOD_LABEL.get(m) ?? m).join(', ');
}

interface Props {
    tenantId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Valor corto en grid: label arriba (chico), valor abajo. */
function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {label}
            </p>
            <div className="break-words text-sm font-medium">{children}</div>
        </div>
    );
}

/** Sección con título y valores en un grid de 2 columnas, en una tarjeta sutil. */
function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="rounded-lg border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
        </section>
    );
}

/**
 * Detalle de solo lectura de una organización: identidad, seguridad, marca y auditoría por nombre;
 * los ids y datos de infraestructura quedan en «Detalles técnicos». Editar y suspender viven en la tabla.
 */
export function TenantDetail({ tenantId, open, onOpenChange }: Props) {
    const queryClient = useQueryClient();

    const detailQ = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: open,
    });
    const data = detailQ.data;

    // Al volver `active`, el backend siembra los roles diferidos; se refresca el detalle y la lista.
    const verifyMut = useMutation({
        mutationFn: () => platformApi.verifyTenantInfra(tenantId),
        onSuccess: async (res) => {
            await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
            if (res.status === 'active') {
                toast.success('Infraestructura lista. Organización activada.');
            } else {
                toast.info('Todavía faltan tablas. Revisa la lista actualizada.');
            }
        },
        onError: handleApiError,
    });

    const { data: applications } = useApplications();
    const levels = useResolvedTenantLevels(data);
    const adminNames = usePlatformAdminNames(open);

    if (!open) return null;

    const applicationName = data?.applicationId
        ? applications?.find((a) => a.applicationId === data.applicationId)?.displayName
        : undefined;
    const connectorArns = data?.connectorArns ? Object.entries(data.connectorArns) : [];
    const mfaEnabled = data?.securityPolicy?.mfaEnabled ?? true;

    const status = data ? STATUS_META[data.status] : null;

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            size="lg"
            title={data?.displayName ?? 'Organización'}
            titleAction={
                status ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={status.variant}>{status.label}</Badge>
                        {applicationName && <Badge variant="default">{applicationName}</Badge>}
                    </div>
                ) : undefined
            }
        >
            {detailQ.isError ? (
                <ErrorState
                    title="No pudimos cargar la organización"
                    message={resolveErrorMessage(detailQ.error)}
                    retryLabel="Reintentar"
                    onRetry={() => {
                        void detailQ.refetch();
                    }}
                />
            ) : !data ? (
                <div className="flex justify-center py-10">
                    <Loading />
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Solo con `pending-infra`: tablas faltantes del silo y re-verificación. */}
                    {data.status === 'pending-infra' && (
                        <section className="rounded-lg border border-warning/40 bg-warning/10 p-4">
                            <h3 className="mb-3 text-sm font-semibold text-foreground">
                                Estado de infraestructura
                            </h3>
                            <Banner variant="warning" className="mb-3">
                                La organización está pendiente de infraestructura. Contacta al
                                equipo de infraestructura para que cree estos recursos.
                            </Banner>
                            <p className="mb-1.5 text-sm font-medium">Tablas faltantes</p>
                            {data.infraRequirements && data.infraRequirements.length > 0 ? (
                                <ul className="space-y-1 rounded-md border bg-muted p-3">
                                    {[...data.infraRequirements]
                                        .sort((a, b) => a.localeCompare(b, 'es'))
                                        .map((t) => (
                                            <li key={t}>
                                                <code className="select-all break-all font-mono text-sm text-foreground">
                                                    {t}
                                                </code>
                                            </li>
                                        ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    No hay tablas pendientes informadas.
                                </p>
                            )}
                            <div className="mt-4 flex justify-end">
                                <Button
                                    isLoading={verifyMut.isPending}
                                    onClick={() => {
                                        verifyMut.mutate();
                                    }}
                                >
                                    Re-verificar infraestructura
                                </Button>
                            </div>
                        </section>
                    )}

                    <Section title="Identidad">
                        <Field label="Nombre">{data.displayName}</Field>
                        <Field label="Aplicación">{applicationName ?? '—'}</Field>
                        <Field label="Estado">
                            <Badge variant={status?.variant ?? 'default'}>
                                {status?.label ?? '—'}
                            </Badge>
                        </Field>
                        <Field label="Niveles">
                            {levels?.length ? (
                                <div className="flex flex-wrap gap-1">
                                    {levels.map((lvl) => (
                                        <Badge key={lvl.level} variant="info">
                                            {lvl.displayName}
                                        </Badge>
                                    ))}
                                </div>
                            ) : (
                                '—'
                            )}
                        </Field>
                    </Section>

                    {data.securityPolicy && (
                        <Section title="Seguridad">
                            <Field label="Verificación en dos pasos">
                                {mfaEnabled ? 'Activada' : 'Desactivada'}
                            </Field>
                            {mfaEnabled && (
                                <Field label="Métodos de verificación">
                                    {mfaMethodNames(data.securityPolicy.methodsAllowed)}
                                </Field>
                            )}
                        </Section>
                    )}

                    {data.brandingConfig && (
                        <Section title="Marca">
                            <Field label="Color primario">
                                {data.brandingConfig.primaryColor ? (
                                    <span className="inline-flex items-center gap-2">
                                        <span
                                            className="inline-block size-4 rounded border"
                                            style={{
                                                backgroundColor: data.brandingConfig.primaryColor,
                                            }}
                                        />
                                        <code className="font-mono text-xs">
                                            {data.brandingConfig.primaryColor}
                                        </code>
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </Field>
                            <Field label="Logo">
                                {data.brandingConfig.logoUrl ? (
                                    <a
                                        href={data.brandingConfig.logoUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-brand-accent underline underline-offset-2"
                                    >
                                        Ver logo
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </Field>
                        </Section>
                    )}

                    <Section title="Auditoría">
                        <Field label="Creado">
                            {data.createdAt ? formatDateMx(data.createdAt) : '—'}
                        </Field>
                        <Field label="Actualizado">
                            {data.updatedAt ? formatDateMx(data.updatedAt) : '—'}
                        </Field>
                        <Field label="Creado por">
                            {actorDisplayName(data.createdBy, adminNames)}
                        </Field>
                        <Field label="Actualizado por">
                            {actorDisplayName(data.updatedBy, adminNames)}
                        </Field>
                    </Section>

                    <TechnicalDetails>
                        <CopyableId
                            label="Identificador de la organización"
                            value={data.tenantId}
                        />
                        <CopyableId label="Región" value={data.cognitoPool.region} />
                        <CopyableId
                            label="Grupo de usuarios"
                            value={data.cognitoPool.displayName}
                        />
                        <CopyableId
                            label="Identificador del grupo de usuarios"
                            value={data.cognitoPool.userPoolId}
                        />
                        <CopyableId
                            label="ARN del grupo de usuarios"
                            value={data.cognitoPool.userPoolArn}
                        />
                        <CopyableId
                            label="Cliente de la aplicación"
                            value={data.cognitoPool.appClientId}
                        />
                        <CopyableId label="Prefijo de tablas" value={data.tablePrefix} />
                        {data.iamRoleArn && (
                            <CopyableId label="ARN del rol de acceso" value={data.iamRoleArn} />
                        )}
                        {data.s3Bucket && (
                            <CopyableId label="Almacenamiento de archivos" value={data.s3Bucket} />
                        )}
                        {connectorArns.map(([name, arn]) => (
                            <CopyableId key={name} label={`Conector · ${name}`} value={arn} />
                        ))}
                    </TechnicalDetails>
                </div>
            )}
        </Modal>
    );
}
